import { useRouteResponse } from "@src/app/hooks/useRouteResponse";
import type { BFFResponseType } from "@src/app/types/bff";
import type { Section } from "@src/app/types/Data";
import type { ArticlePageResponse } from "@src/app/types/response";
import clsx from "clsx";
import { useEffect, useState } from "react";

import Link from "../Link";
import SectionContainer from "../SectionContainer";
import { SliceTag } from "../SectionTag";

/**
 * Article Recommend Articles 猜你喜欢
 */
export default function ArticleRecommend() {
  const { article } = useRouteResponse("articlePage");

  const [recommendArticles, setRecommendArticles] = useState<
    ArticlePageResponse["recommendArticles"]
  >([]);

  const getInfo = async () => {
    if (!article?.id) {
      console.warn("No article ID found for fetching recommended articles.");
      return;
    }
    try {
      const res = await fetch(
        `/_plat/api/v1/client/recommendArticles/${article.id}`,
      );
      const data = (await res.json()) as BFFResponseType<ArticlePageResponse>;

      setRecommendArticles(data.response.recommendArticles || []);
    } catch (error) {
      console.error("Error fetching recommended articles:", error);
    }
  };

  useEffect(() => {
    void getInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!recommendArticles?.length) {
    return null;
  }

  return (
    <SectionContainer title="猜你喜欢" className="-mx-3 mb-4">
      <div className="mx-3 mt-4 grid grid-cols-12 gap-x-6 gap-y-8">
        {recommendArticles.map((article) => {
          return (
            <div
              key={article.id}
              className="group relative col-span-12 bg-white px-3 py-4 shadow-[3px_3px_5px_0px_rgba(0,0,0,0.07)] lg:col-span-6"
            >
              <Link
                to={`${article.urlPath}?ref=article-recommended-contents`}
                className="flex flex-col gap-2"
              >
                <SliceTag
                  section={article.section as Exclude<Section, "home">}
                />

                <h3
                  className={clsx(
                    "line-clamp-3 text-header-3 text-primary lg:group-hover:underline",
                  )}
                >
                  {article.title}
                </h3>

                <h4
                  className="line-clamp-1 min-h-[22px] text-subheader text-tertiary"
                  dangerouslySetInnerHTML={{ __html: article.description }}
                />
              </Link>
            </div>
          );
        })}
      </div>
    </SectionContainer>
  );
}
